// useChefGPT — Client hook to call the ChefGPT edge function proxy
// Provides:
//   fetchRecipeImages(meals)          → batch-fetches photos and applies to store
//   fetchRecipeFromIngredients(items) → returns a recipe object

import { useCallback, useRef } from 'react';
import { useAppStore } from './useAppStore';

const CHEFGPT_URL  = 'https://pgjncthsfxsylmvkgibx.supabase.co/functions/v1/chefgpt';
const MEALPLAN_URL = 'https://pgjncthsfxsylmvkgibx.supabase.co/functions/v1/meal-plan';
const ANON_KEY     = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBnam5jdGhzZnhzeWxtdmtnaWJ4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcxMDEwMTEsImV4cCI6MjA5MjY3NzAxMX0.PDtIhTOuZNnwOqNkdOD8eTC6SCXdlPT5H7vrcU30UXk';

const HEADERS = {
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${ANON_KEY}`,
  'apikey': ANON_KEY,
};

export interface ChefGPTRecipe {
  name:         string;
  description?: string;
  servings?:    number;
  prep_time?:   string;
  cook_time?:   string;
  ingredients?: { name: string; quantity?: string; unit?: string }[];
  instructions?: string[];
  image_url?:   string;
}

export function useChefGPT() {
  const updateMealPhotos = useAppStore(s => s.updateMealPhotos);
  const postalCode       = useAppStore(s => s.postalCode);
  const selectedStore    = useAppStore(s => s.selectedStore);
  const fetchingRef = useRef(false);

  /**
   * Batch-fetch recipe images for every meal in the plan.
   * Uses TheMealDB — 100% FREE, no API key, no Anthropic credits needed.
   * Runs all requests in parallel (max 8 concurrent) for speed.
   * Called once after a meal plan is returned from the edge function.
   */
  const fetchRecipeImages = useCallback(async (
    meals: { id: string; name: string; photo_url?: string; tagline?: string; ingredients?: { name: string; on_sale?: boolean }[]; method?: string }[]
  ) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    // Only fetch meals that don't already have a photo_url
    const toFetch = meals.filter(m => !m.photo_url);
    if (toFetch.length === 0) { fetchingRef.current = false; return; }

    // Fetch all images in parallel — TheMealDB is free and handles concurrent requests fine
    // Cap at 8 concurrent to be a good citizen
    const CONCURRENCY = 8;
    const photos: Record<string, string> = {};

    for (let i = 0; i < toFetch.length; i += CONCURRENCY) {
      const batch = toFetch.slice(i, i + CONCURRENCY);
      await Promise.allSettled(batch.map(async meal => {
        try {
          const saleIngs = (meal.ingredients ?? []).filter(i => i.on_sale).map(i => i.name);
          const allIngs  = (meal.ingredients ?? []).map(i => i.name);
          const keyIngs  = [...new Set([...saleIngs, ...allIngs])].slice(0, 4);

          // generate_recipe_image uses TheMealDB (free) + optional Supabase Storage save
          const res = await fetch(MEALPLAN_URL, {
            method: 'POST',
            headers: HEADERS,
            body: JSON.stringify({
              action:      'generate_recipe_image',
              meal_id:     meal.id,
              name:        meal.name,
              tagline:     meal.tagline     ?? '',
              ingredients: keyIngs,
              method:      meal.method      ?? '',
              merchant:    selectedStore?.name ?? '',
              postalCode:  postalCode ?? 'M5V3A8',
            }),
          });

          if (res.ok) {
            const data = await res.json();
            const url = data?.url ?? null;
            if (url && typeof url === 'string') {
              photos[meal.id] = url;
              // Apply photo to store immediately so it renders as soon as it arrives
              updateMealPhotos({ [meal.id]: url });
            }
          } else {
            const err = await res.json().catch(() => ({}));
            console.warn(`[useChefGPT] image gen failed for "${meal.name}":`, err?.error ?? res.status);
          }
        } catch (e) {
          console.warn(`[useChefGPT] image gen error for "${meal.name}":`, e);
        }
      }));
    }

    // Belt-and-suspenders: patch photos into the cached plan
    if (Object.keys(photos).length && selectedStore?.name && postalCode) {
      fetch(MEALPLAN_URL, {
        method: 'POST',
        headers: HEADERS,
        body: JSON.stringify({
          action: 'patch_photos',
          postalCode,
          merchant: selectedStore.name,
          photos,
        }),
      }).catch(() => {});
    }

    fetchingRef.current = false;
    return photos;
  }, [updateMealPhotos, postalCode, selectedStore]);

  /**
   * Fetch a single recipe based on pantry ingredients.
   * Returns the parsed recipe or throws on error.
   */
  const fetchRecipeFromIngredients = useCallback(async (
    ingredients: string[],
    servings = 4
  ): Promise<ChefGPTRecipe> => {
    const res = await fetch(CHEFGPT_URL, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify({ action: 'recipe_from_ingredients', ingredients, servings }),
    });
    const data = await res.json();
    if (!res.ok || data.error) throw new Error(data.error ?? `ChefGPT error ${res.status}`);
    return data as ChefGPTRecipe;
  }, []);

  return { fetchRecipeImages, fetchRecipeFromIngredients };
}
